<script setup>
    // Component
    import MainHeader from '@/components/HeaderComponent.vue';
    import SideMenu from '@/components/ManagementSideMenuComponent.vue';
    import detail from '@/components/modal/ManagementDetailComponent.vue';
    import contract from '@/components/modal/ManagementContractComponent.vue';
    import userSelect from '@/components/modal/UserSelectComponent.vue';
    import { ref, onMounted, inject, reactive, watch, getCurrentInstance } from 'vue';
    import { ManagementListRequest } from '@/models/management/managementModel';
    import { useManagementListStore } from '@/stores/management/managementListStore';
    import { useFileDownloadStore } from '@/stores/common/fileDownloadStore';
    import { useFileUploadStore } from '@/stores/common/fileUploadStore';
    import fileUploadDownloadService from '@/commonProject/service/fileUploadDownloadService';
    import Pagination from '@/components/PaginationComponent.vue';
    import i18n from '@/common/i18n';
    import utils from '@/commonProject/helpers/utils';

    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    const pagination = ref();

    // 利用者一覧ストア
    const managementListStore = useManagementListStore();
    // ファイルダウンロードストア
    const fileDownloadStore = useFileDownloadStore();
    // ファイルアップロードストア
    const fileUploadStore = useFileUploadStore();
    // 利用者一覧検索項目
    const managementListRequest = ref({});
    // 利用者一覧検索済み項目
    const managementListRequestDone = ref({});
    // 検索欄:表示フラグ
    const moveinState = ref(false);
    // 検索欄:非表示フラグ
    const moveoutState = ref(false);
    // 検索欄:組織セレクトボックス内容リスト
    let organizationIssue = reactive([]);
    // 組織:選択した組織情報
    //const selectOrganization = ref();
    // 組織:「選択してください」表示フラグ
    //const selectFlg = ref(false);
    // 組織:第2階層PK
    //const layer2Pk = ref('');
    // 組織:第3階層PK
    //const layer3Pk = ref('');
    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');
    // ページングパーツ
    const currentPage = ref(1);
    const totalPages = ref(0);
    // 検索欄:性別
    const genderInfo = [
        { name: i18n.global.t('ManagementSelect.parts.dl1'), value: '1' },
        { name: i18n.global.t('ManagementSelect.parts.dl2'), value: '2' },
        { name: i18n.global.t('ManagementSelect.parts.dl3'), value: '3' },
        { name: i18n.global.t('ManagementSelect.parts.dl4'), value: 'N' },
    ];
    // 検索欄:管理者の登録
    const mngFlagInfo = [
        { name: i18n.global.t('ManagementSelect.parts.dl5'), value: true },
        { name: i18n.global.t('ManagementSelect.parts.dl6'), value: false },
    ];
    // 検索欄:使用言語
    const languageInfo = [
        { name: i18n.global.t('ManagementSelect.parts.dl7'), value: '1' },
        { name: i18n.global.t('ManagementSelect.parts.dl8'), value: '2' },
        { name: i18n.global.t('ManagementSelect.parts.dl4'), value: 'N' },
    ];
    // 検索欄:在職ステータス
    const incumbentStatusInfo = [
        { name: i18n.global.t('ManagementSelect.parts.dl9'), value: '1' },
        { name: i18n.global.t('ManagementSelect.parts.dl10'), value: '2' },
        { name: i18n.global.t('ManagementSelect.parts.dl11'), value: '3' },
        { name: i18n.global.t('ManagementSelect.parts.dl4'), value: 'N' },
    ];

    // ダブルクリック対策
    const isPageDoubleClickBlocked = ref(false);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    /**
     * 初期処理
     */
    onMounted(async () => {
        // ダブルクリック対策フラグを初期化する。
        isPageDoubleClickBlocked.value = false;
        // 企業契約情報取得のStoreを初期化する。
        managementListStore.reset();
        // 利用者一覧検索項目の初期化
        managementListRequest.value = new ManagementListRequest();
        // 利用者一覧検索済み項目の初期化
        managementListRequestDone.value = new ManagementListRequest();
        // セレクトボックス内容取得(検索欄)
        await getIssue();
        // 利用者一覧取得
        await getUserList();
    });

    /**
     * 利用者一覧取得処理
     */
    async function getUserList() {
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        const maxPage = 50;
        const requestBody = {
            Cond: managementListRequest.value,
            PageCond: {
                RowPosition: maxPage * (currentPage.value - 1) + 1,
                RowAmount: maxPage,
                RowCount: true,
            },
        };
        const response = await managementListStore.postCliLstAction(API_URL, requestBody);

        if (response.success) {
            totalPages.value = response.RowResult;
            await handleOnloadPagination(totalPages.value);
            managementListRequestDone.value = managementListRequest.value;
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * ページ操作
     * @param newPage
     */
    async function handlePageChange(newPage) {
        currentPage.value = newPage;

        // ページング実行により、一覧取得APIを再実行する。
        await getUserList();
    }
    /**
     * ページングの初期処理を実行する。
     * 取得したAPIのデータ数を引き渡す。
     */
    async function handleOnloadPagination(RowResult) {
        pagination.value.onload(RowResult);
    }

    /**
     * 検索欄セレクトボックス内容取得処理
     */
    async function getIssue() {
        // 組織セレクトボックス内容取得
        await getOrganizationIssue();
        // 職種セレクトボックス内容取得
        await postJobLstAction();
        // 職位セレクトボックス内容取得
        await postPosLstAction();
        // 雇用形態セレクトボックス内容取得
        await postEtpLstAction();
        // 勤務地セレクトボックス内容取得
        await postLocLstAction();
        // 時間外労働時間セレクトボックス内容取得
        await postOvtLstAction();
    }

    /**
     * 職種セレクトボックス内容取得処理
     */
    async function postJobLstAction() {
        const requestBody = {
            PageCond: { RowPosition: 0, RowAmount: null, RowCount: true },
        };
        const response = await managementListStore.postJobLstAction(API_URL, requestBody);

        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 職位セレクトボックス内容取得処理
     */
    async function postPosLstAction() {
        const requestBody = {
            PageCond: { RowPosition: 0, RowAmount: null, RowCount: true },
        };
        const response = await managementListStore.postPosLstAction(API_URL, requestBody);

        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 雇用形態セレクトボックス内容取得処理
     */
    async function postEtpLstAction() {
        const requestBody = {
            PageCond: { RowPosition: 0, RowAmount: null, RowCount: true },
        };
        const response = await managementListStore.postEtpLstAction(API_URL, requestBody);

        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 勤務地セレクトボックス内容取得処理
     */
    async function postLocLstAction() {
        const requestBody = {
            PageCond: { RowPosition: 0, RowAmount: null, RowCount: true },
        };
        const response = await managementListStore.postLocLstAction(API_URL, requestBody);

        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 時間外労働時間セレクトボックス内容取得処理
     */
    async function postOvtLstAction() {
        const requestBody = {
            PageCond: { RowPosition: 0, RowAmount: null, RowCount: true },
        };
        const response = await managementListStore.postOvtLstAction(API_URL, requestBody);

        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * // 組織セレクトボックス内容取得処理
     */
    async function getOrganizationIssue() {
        // Storeを初期化する。
        managementListStore.organizationIssue = [];
        managementListStore.organizationInfo = {};
        organizationIssue.splice(0);

        // 現在の親組織IDの座標を初期化する。
        // 親組織のプルダウンの内容に応じて、座標を制御する。
        let parentLevel = 0;

        // 親組織プルダウンが変更されて、現在の親組織のPK情報が取得できたか判定する。
        if (!utils.isNullEmpty(managementListRequest.value.OrganizationPk)) {
            // 親組織の組織名を取得するために、親組織PKをキーに、企業組織取得APIをコールする。
            //await getOrganizationInfo(organizationInfoRequest.value.OrganizationPk);

            const queryParam = {
                OrganizationPk: managementListRequest.value.OrganizationPk,
            };
            const response = await managementListStore.getOrgDtlForParenAction(API_URL, queryParam);
            if (!response.success) {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }

            // 取得した親組織の階層レベルを格納する。
            parentLevel = managementListStore.organizationParentInfo.OrganizationLv;
        }

        // 現在の座標が取得できなかった場合。つまり、第1階層まで戻ってきた場合。
        if (parentLevel === 0) {
            // 第1階層のルート。

            // 初期値は選択してくださいを設定する。
            const defalutOption = {
                pk: '',
                name: i18n.global.t('ManagementSearch.parts.dl20'),
            };
            organizationIssue.push(defalutOption);

            // 選択してくださいを初期値に設定する。
            managementListRequest.value.OrganizationPk = '';

            // 第1階層の組織データをプルダウンに生成するためのリクエストボディ条件を制御する。
            const requestBody = {};

            // 企業組織選択APIをコールする。
            const response = await managementListStore.getOrgSelAction(API_URL, requestBody);

            if (response.success) {
                if (!utils.isNullEmptyArray(managementListStore.organizationIssue)) {
                    for (const org of managementListStore.organizationIssue) {
                        // 第1階層に属する組織の選択肢を生成する。
                        const option = {
                            pk: org.pk,
                            name: org.name,
                        };
                        organizationIssue.push(option);
                    }
                }
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        } else {
            // 第1階層以外の場合。

            // 上の階層に戻るの選択肢。
            // 自分の親の親階層に戻る。
            const backToParentOrgOption = {
                pk: utils.changeNullValueToEmpty(managementListStore.organizationParentInfo.ParentOrganizationPk),
                name: i18n.global.t('ManagementSearch.parts.dl21'),
            };
            organizationIssue.push(backToParentOrgOption);

            // 自分の親階層の情報を設定する。
            const defalutOption = {
                pk: managementListRequest.value.OrganizationPk,
                name: managementListStore.organizationParentInfo.OrganizationNmJ,
            };
            organizationIssue.push(defalutOption);

            // 企業組織選択APIのクエリパラメーターを生成する。
            // 座標は、自分自身の組織PKを設定する。
            const queryParam = {
                OrganizationPk: managementListRequest.value.OrganizationPk,
            };

            // 企業組織選択APIをコールする。
            const response2 = await managementListStore.getOrgSelAction(API_URL, queryParam);
            if (response2.success) {
                if (!utils.isNullEmptyArray(managementListStore.organizationIssue)) {
                    // 自分自身の組織PKで無ければ、全部の自階層と同一の組織をプルダウン選択肢に生成する。
                    for (const org of managementListStore.organizationIssue) {
                        const option = {
                            pk: org.pk,
                            name: '　' + org.name,
                        };
                        organizationIssue.push(option);
                    }
                }
            } else {
                messageDialog('error', response2.errorMessage);
                if (response2.redirect) {
                    location.href = response2.redirectUrl;
                }
                return;
            }
        }
    }

    /**
     * CSVダウンロード処理
     */
    async function downloadUCsv() {
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // ダブルクリックを無効化する。
        if (isPageDoubleClickBlocked.value) {
            return;
        }

        // ダブルクリック対策フラグを有効にする。
        isPageDoubleClickBlocked.value = true;

        const requestBody = {
            Cond: managementListRequestDone.value,
        };
        const response = await managementListStore.postCliDldAction(API_URL, requestBody);
        if (response.success) {
            const DldHistPk = response.responseBody.DldHistPk;
            if (!utils.isNullEmpty(DldHistPk)) {
                // ダウンロード履歴取得APIのクエリパラメーターを生成する。
                const queryParamDldHistGet = {
                    DldHistPk: DldHistPk, //ダウンロード履歴PK
                };

                // 念のためタイマーイベントを全てクリアする。
                // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                clearTimeout();

                // タイマーで監視し、APIのエラー時のループを制御する。
                let isDownloadTimeoutError = false;

                // ダウンロード処理開始後からダウンロード処理が完了せずに15分経過した場合は、エラーとしてダウンロード処理を停止する。
                const downloadTotalErrorTimer = 900000;

                // ダウンロード履歴取得APIをコールする間隔のタイマー値を設定する。
                // ダウンロード可能かどうかのチェック処理後、2秒間のインターバルを設けて、再度APIをコールする。
                const callDownloadHistoryApiintervalTimer = 2000;

                // ダウンロード対象のファイル名を宣言する。
                const fileName = 'user.csv';

                // ダウンロード処理の総経過時間を監視し、15分経過した場合に呼び出されるファンクションを宣言する。
                const checkTimeout = function () {
                    // ダウンロード総経過時間監視フラグを有効にして、ダウンロード限界時間経過としてエラー発生扱いにする。
                    isDownloadTimeoutError = true;

                    // 念のためタイマーイベントを全てクリアする。
                    // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                    clearTimeout();
                };

                // ダウンロード処理構造体を宣言する。
                // ダウンロード履歴取得API、ダウンロード履歴更新APIの実行タイミングを制御し、ダウンロード可能なファイルが存在する場合はJavascriptでダウンロードを実行する。
                const downloadEventPromise = async function (id) {
                    await new Promise(function (resolve, reject) {
                        // ダウンロード履歴取得APIから、ダウンロード対象ファイルの状態を取得し、本処理で判定して制御する。

                        // もし、ダウンロード総経過時間フラグが有効の場合は、以降の処理を実行せず、ダウンロード限界時間経過としてエラーにして、rejectで返却する。
                        if (isDownloadTimeoutError) {
                            reject('download timeout error');
                            return;
                        }

                        // ダウンロード履歴取得APIの判定したダウンロードファイルの状態に応じて制御する。
                        // ファイルダウンロード処理Javascript側で状況を判定している。
                        switch (id) {
                            case 'continue':
                            case 'startDownload':
                            case 'endDownload':
                                // ダウンロード準備中。
                                // ダウンロード可能状態の応答が返ってきた。
                                // Javascriptでファイルをダウンロードして、正常に完了した状態。
                                resolve(id);
                                break;
                            case 'error':
                            case 'break':
                            default:
                                // ダウンロード履歴取得APIでダウンロードエラーが発生。
                                // ダウンロード履歴取得APIからダウンロード完了が返却された場合。
                                // 上記以外の本処理内での例外の場合。
                                // ダウンロード処理を停止するためにrejectで返却する。
                                reject(id);
                                break;
                        }
                    })
                        .then(async function (resolved) {
                            // Promiseでresolveの正常応答の場合。

                            // もし、ダウンロード総経過時間フラグが有効の場合は、以降の処理を実行しない。
                            if (isDownloadTimeoutError) {
                                return;
                            }

                            // ダウンロード履歴取得APIから取得したステータスと、ダウンロード処理スクリプトで判定した結果に応じて制御する。
                            switch (resolved) {
                                case 'startDownload':
                                    {
                                        // ダウンロード履歴取得APIからダウンロード可能ステータスが返却されたので、Javascriptでファイルダウンロードを実行した場合。

                                        // ダウンロード履歴更新APIのリクエストボディを生成する。
                                        const requestBodyDldHistPut = {
                                            DldHistPk: DldHistPk,
                                            DldStatus: '3', // ダウンロード中モードに設定する。
                                        };

                                        // ダウンロード履歴更新APIをコールする。
                                        const responseDldHistPut = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL, requestBodyDldHistPut);

                                        // APIの応答結果に応じて制御する。
                                        if (responseDldHistPut.success) {
                                            // APIの正常応答を検知して、再度本ダウンロードPromiseを実行する。
                                            await downloadEventPromise('continue');
                                        } else {
                                            // エラーの場合。
                                            messageDialog('error', responseDldHistPut.errorMessage);
                                            if (responseDldHistPut.redirect) {
                                                location.href = responseDldHistPut.redirectUrl;
                                            }
                                        }
                                    }
                                    break;
                                case 'endDownload':
                                    {
                                        // Javascriptでファイルダウンロードを実行した場合。

                                        // ダウンロード履歴更新APIのリクエストボディを生成する。
                                        const requestBodyDldHistPut = {
                                            DldHistPk: DldHistPk,
                                            DldStatus: '4', // ダウンロード完了に設定する。
                                        };

                                        // ダウンロード履歴更新APIをコールする。
                                        const responseDldHistPut = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL, requestBodyDldHistPut);

                                        // APIの応答結果に応じて制御する。
                                        if (responseDldHistPut.success) {
                                            // APIの正常応答を検知して、再度本ダウンロードPromiseを実行する。
                                            await downloadEventPromise('continue');
                                        } else {
                                            // エラーの場合。
                                            messageDialog('error', responseDldHistPut.errorMessage);
                                            if (responseDldHistPut.redirect) {
                                                location.href = responseDldHistPut.redirectUrl;
                                            }
                                        }
                                    }
                                    break;
                                case 'continue':
                                    // ダウンロード履歴取得APIを指定のインターバルの時間経過後に再度コールするタイマーイベント。
                                    setTimeout(async function () {
                                        // ダウンロード履歴取得APIをコールする。
                                        const responseDldHistGet2 = await fileDownloadStore.getDownloadFileHistoryPkFromApi(API_URL, queryParamDldHistGet);

                                        // APIの応答結果に応じて制御する。
                                        if (responseDldHistGet2.success) {
                                            // ファイルダウンロードスクリプトを実行する。
                                            // ダウンロード履歴取得APIから取得したダウンロード状態、ダウンロードファイルの座標、ダウンロード対象のファイル名を引数に設定する。
                                            const fileUploadDownloadServiceResult2 = await fileUploadDownloadService.fileDownload(responseDldHistGet2.responseBody.DldStatus, responseDldHistGet2.responseBody.DldS3Url, fileName);

                                            // ファイルダウンロードスクリプト実行後、再度本ダウンロードPromiseを実行する。
                                            await downloadEventPromise(fileUploadDownloadServiceResult2);
                                        } else {
                                            // ダウンロード履歴取得APIがエラーの場合。
                                            messageDialog('error', responseDldHistGet2.errorMessage);
                                            if (responseDldHistGet2.redirect) {
                                                location.href = responseDldHistGet2.redirectUrl;
                                            }
                                        }
                                    }, callDownloadHistoryApiintervalTimer);
                                    break;
                                default:
                                    // 何もしない
                                    break;
                            }
                        })
                        .catch(function (/*rejected*/) {
                            // Promiseでrejectのルートで処理された場合。

                            // ダウンロードが完了した、や、エラーが発生したので、ダウンロード総経過時間フラグを更新して、以降の処理を実行しないようにする。
                            isDownloadTimeoutError = true;

                            // 念のためタイマーイベントを全てクリアする。
                            // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                            clearTimeout();
                        });
                };

                // ***** ダウンロードの初期処理を実行する - ここから - **** //

                // ダウンロード総時間を監視する。
                setTimeout(checkTimeout, downloadTotalErrorTimer);

                // 初回のダウンロード履歴取得APIをコールする。
                const responseDldHistGet = await fileDownloadStore.getDownloadFileHistoryPkFromApi(API_URL, queryParamDldHistGet);

                // APIの応答結果に応じて制御する。
                if (responseDldHistGet.success) {
                    // ファイルダウンロードスクリプトを実行する。
                    // ダウンロード履歴取得APIから取得したダウンロード状態、ダウンロードファイルの座標、ダウンロード対象のファイル名を引数に設定する。
                    const fileUploadDownloadServiceResult = await fileUploadDownloadService.fileDownload(responseDldHistGet.responseBody.DldStatus, responseDldHistGet.responseBody.DldS3Url, fileName);

                    // ファイルダウンロードスクリプト実行後、ファイルダウンロード処理Promiseを実行する。
                    await downloadEventPromise(fileUploadDownloadServiceResult);
                } else {
                    // ダウンロード履歴取得APIがエラーの場合。
                    messageDialog('error', responseDldHistGet.errorMessage);
                    if (responseDldHistGet.redirect) {
                        location.href = responseDldHistGet.redirectUrl;
                    }
                }

                // ***** ダウンロードの初期処理を実行する - ここまで - **** //
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }

        // ダブルクリック対策フラグを初期化する。
        isPageDoubleClickBlocked.value = false;
    }

    /**
     * CSVアップロード処理
     */
    const selectedUploadFile = ref([]);
    async function uploadCsv(e) {
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        const files = e.target.files || e.dataTransfer.files;
        const uploadTargetFile = {
            name: files[0].name,
            type: files[0].type,
            data: files[0],
        };

        // 選択したファイルを初期化する。
        e.target.value = '';

        // データ格納前に初期化する。
        selectedUploadFile.value = [];

        selectedUploadFile.value.push(uploadTargetFile);
        if (selectedUploadFile.value.length > 0) {
            for (let i = 0; i < selectedUploadFile.value.length; i++) {
                const data = selectedUploadFile.value[i];

                const requestBody = {
                    FileNm: data.name,
                };

                // ファイルアップロードAPIをコールする。
                const response = await managementListStore.postCliUldAction(API_URL, requestBody);
                if (response.success) {
                    const queryParam3 = {
                        UldHistPk: response.responseBody.UldHistPk,
                    };
                    // アップロード履歴取得APIをコールする。
                    const response2 = await fileUploadStore.getUploadFileHistoryPkFromApi(API_URL, queryParam3);
                    if (response2.success) {
                        const fileUploadResults = await fileUploadDownloadService.fileUpload(response2.responseBody.UldS3Url, data.data);

                        if (fileUploadResults.status === 200) {
                            const requestBody3 = {
                                UldHistPk: response.responseBody.UldHistPk,
                                UldStatus: '2',
                            };
                            // アップロード履歴更新APIをコールする。
                            const response3 = await fileUploadStore.updateUploadFileHistoryPkFromApi(API_URL, requestBody3);
                            if (!response3.success) {
                                messageDialog('error', response3.errorMessage);
                                if (response3.redirect) {
                                    location.href = response3.redirectUrl;
                                }
                            }
                        }
                    } else {
                        messageDialog('error', response2.errorMessage);
                        if (response2.redirect) {
                            location.href = response2.redirectUrl;
                        }
                    }
                } else {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                }
            }
        }
    }

    /**
     * 日付設定処理
     */
    const selectedDate1 = ref(''); // 生年月日の開始日
    const selectedDate2 = ref(''); // 生年月日の終了日
    const selectedDate3 = ref(''); // 入社年月日の開始日
    const selectedDate4 = ref(''); // 入社年月日の終了日
    const selectedDate5 = ref(''); // 退職年月日の開始日
    const selectedDate6 = ref(''); // 退職年月日の終了日

    // ----------------------------
    // watch
    // ----------------------------
    watch(selectedDate1, (newValue) => {
        if (newValue) {
            managementListRequest.value.BirthdayS = convertDate(newValue);
        } else {
            managementListRequest.value.BirthdayS = '';
        }
    });
    watch(selectedDate2, (newValue) => {
        if (newValue) {
            managementListRequest.value.BirthdayE = convertDate(newValue);
        } else {
            managementListRequest.value.BirthdayE = '';
        }
    });
    watch(selectedDate3, (newValue) => {
        if (newValue) {
            managementListRequest.value.JoinedDateS = convertDate(newValue);
        } else {
            managementListRequest.value.JoinedDateS = '';
        }
    });
    watch(selectedDate4, (newValue) => {
        if (newValue) {
            managementListRequest.value.JoinedDateE = convertDate(newValue);
        } else {
            managementListRequest.value.JoinedDateE = '';
        }
    });
    watch(selectedDate5, (newValue) => {
        if (newValue) {
            managementListRequest.value.LeavedDateS = convertDate(newValue);
        } else {
            managementListRequest.value.LeavedDateS = '';
        }
    });
    watch(selectedDate6, (newValue) => {
        if (newValue) {
            managementListRequest.value.LeavedDateE = convertDate(newValue);
        } else {
            managementListRequest.value.LeavedDateE = '';
        }
    });

    // 日付アレンジ用変換メソッド
    function convertDate(input) {
        if (utils.isNullEmpty(input)) {
            return '';
        }
        const [year, month, day] = input.split('-');
        return `${year}/${month}/${day}`;
    }

    /**
     * 検索欄の表示処理
     */
    async function changeMoveinState() {
        moveoutState.value = false;
        moveinState.value = true;
    }

    /**
     * 検索欄の非表示処理
     */
    async function changeMooveoutState() {
        moveinState.value = false;
        moveoutState.value = true;
    }

    const detailDispFlg = ref('');
    const detailComponent = ref();
    /**
     * 利用者詳細モーダル
     * @param UserPk
     */
    function openDetail(UserPk) {
        detailComponent.value.setInfo(UserPk);
        detailDispFlg.value = 'block';
    }

    function closeDetail() {
        detailDispFlg.value = '';
    }
    /**
     * 利用者契約割り当てモーダル
     */
    const contractDispFlg = ref('');
    const contractComponent = ref();
    function openContract() {
        contractComponent.value.setInfo(managementListRequestDone.value);
        contractDispFlg.value = 'block';
    }
    function closeContract() {
        contractDispFlg.value = '';
    }
    /**
     * 利用者の登録や更新が完了したので、利用者一覧取得を再実行する。
     */
    async function closeContractAndUpdateList() {
        contractDispFlg.value = '';

        // 1ページ目に座標を戻す。
        currentPage.value = 1;

        // 一覧を取得しなおす。
        await getUserList();
    }

    /**
     * ユーザー選択モーダル
     */
    const userSelectDispFlg = ref('');
    const userSelectComponent = ref();
    function disclosureCounselingUserSelectOpen() {
        userSelectComponent.value.openModal(API_URL, '2');
        userSelectDispFlg.value = 'block';
    }

    function userSelectClose() {
        userSelectDispFlg.value = '';
    }
    /**
     * ユーザー選択の選択処理
     * @param UserPk
     * @param UserNm
     */
    function userSelected(UserPk) {
        // ユーザ選択
        detailComponent.value.selectModal(UserPk);
        detailDispFlg.value = 'block';
        userSelectDispFlg.value = '';

        //detailComponent.value.setInfo(UserPk);
        //detailDispFlg.value = 'block';
        //openDetail(UserPk);
    }

    /**
     * 利用者の登録や更新が完了したので、利用者一覧取得を再実行する。
     */
    async function closeDetailAndUpdateList() {
        detailDispFlg.value = '';

        // 1ページ目に座標を戻す。
        currentPage.value = 1;

        // 一覧を取得しなおす。
        await getUserList();
    }
</script>

<template>
    <section id="overlay" :style="{ display: userSelectDispFlg }">
        <userSelect ref="userSelectComponent" @userSelectModal:close="userSelectClose" @userSelectModal:select="userSelected" />
    </section>
    <!--ユーザー選択モーダル-->
    <section id="overlay" :style="{ display: detailDispFlg }">
        <detail ref="detailComponent" @detailModal:cancel="closeDetail" @detailModal:closeAndUpdate="closeDetailAndUpdateList" />
    </section>
    <!--利用者詳細モーダル-->
    <section id="overlay" :style="{ display: contractDispFlg }">
        <contract ref="contractComponent" @contractModal:cancel="closeContract" @contractModal:closeAndUpdate="closeContractAndUpdateList" />
    </section>
    <!--割り当てモーダル-->
    <section id="main">
        <MainHeader />
        <div id="contents" class="sideMenu headerSingleBlock">
            <SideMenu />
            <div id="rightBlock" class="noPaddingTop">
                <section id="fillter" :class="{ movein: moveinState, moveout: moveoutState }">
                    <section id="fillterHeader">
                        <h2>{{ $t('ManagementSearch.parts.pageTitle1') }}</h2>
                        <button type="button" id="closefillterBtn" @click="changeMooveoutState">&nbsp;</button>
                    </section>
                    <section id="fillterBody">
                        <section class="scroll">
                            <input type="text" id="search" v-model="managementListRequest.KeyWord" />
                            <!--キーワード入力欄-->
                            <dl>
                                <!--生年月日-->
                                <dt>
                                    <span>{{ $t('ManagementSearch.parts.dl1') }}</span>
                                </dt>
                                <dd>
                                    <div class="filterBox">
                                        <span class="text">{{ $t('ManagementSearch.parts.dl2') }} </span>
                                        <label class="calendar2">
                                            <input type="date" :class="[selectedDate1 !== '' ? 'fillterText' : 'fillterText2 blank']" placeholder="日付を選択してください" v-model="selectedDate1" />
                                        </label>
                                    </div>
                                    <div class="filterBox">
                                        <span class="text">{{ $t('ManagementSearch.parts.dl3') }} </span>
                                        <label class="calendar2">
                                            <input type="date" :class="[selectedDate2 !== '' ? 'fillterText' : 'fillterText2 blank']" placeholder="日付を選択してください" v-model="selectedDate2" />
                                        </label>
                                    </div>
                                </dd>
                            </dl>
                            <dl>
                                <!--性別-->
                                <dt>
                                    <span>{{ $t('ManagementSearch.parts.dl4') }} </span>
                                </dt>
                                <dd>
                                    <ul class="flex">
                                        <li v-for="(data, index) in genderInfo" :key="index">
                                            <input type="checkbox" :id="'data1-' + (index + 1)" class="checkbox" v-model="managementListRequest.Genders" :value="data.value" />
                                            <label class="checkbox label" :for="'data1-' + (index + 1)">{{ data.name }}</label>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl>
                                <!--管理者の登録-->
                                <dt>
                                    <span>{{ $t('ManagementSearch.parts.dl5') }} </span>
                                </dt>
                                <dd>
                                    <ul class="flex" style="justify-content: left">
                                        <li v-for="(data, index) in mngFlagInfo" :key="index">
                                            <input type="checkbox" :id="'data4-' + (index + 1)" class="checkbox" v-model="managementListRequest.MngFlags" :value="data.value" />
                                            <label class="checkbox label" :for="'data4-' + (index + 1)">{{ data.name }}</label>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl>
                                <!--使用言語-->
                                <dt>
                                    <span>{{ $t('ManagementSearch.parts.dl6') }} </span>
                                </dt>
                                <dd>
                                    <ul class="flex">
                                        <li v-for="(data, index) in languageInfo" :key="index">
                                            <input type="checkbox" :id="'data3-' + (index + 1)" class="checkbox" v-model="managementListRequest.Languages" :value="data.value" />
                                            <label class="checkbox label" :for="'data3-' + (index + 1)">{{ data.name }}</label>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl>
                                <!--入社年月日-->
                                <dt>
                                    <span>{{ $t('ManagementSearch.parts.dl7') }} </span>
                                </dt>
                                <dd>
                                    <div class="filterBox">
                                        <span class="text">{{ $t('ManagementSearch.parts.dl8') }} </span>
                                        <label class="calendar2">
                                            <input type="date" :class="[selectedDate3 !== '' ? 'fillterText' : 'fillterText2 blank']" placeholder="日付を選択してください" v-model="selectedDate3" />
                                        </label>
                                    </div>
                                    <div class="filterBox">
                                        <span class="text">{{ $t('ManagementSearch.parts.dl9') }} </span>
                                        <label class="calendar2">
                                            <input type="date" :class="[selectedDate4 !== '' ? 'fillterText' : 'fillterText2 blank']" placeholder="日付を選択してください" v-model="selectedDate4" />
                                        </label>
                                    </div>
                                </dd>
                            </dl>
                            <dl>
                                <!--退職年月日-->
                                <dt>
                                    <span>{{ $t('ManagementSearch.parts.dl10') }} </span>
                                </dt>
                                <dd>
                                    <div class="filterBox">
                                        <span class="text">{{ $t('ManagementSearch.parts.dl11') }} </span>
                                        <label class="calendar2">
                                            <input type="date" :class="[selectedDate5 !== '' ? 'fillterText' : 'fillterText2 blank']" placeholder="日付を選択してください" v-model="selectedDate5" />
                                        </label>
                                    </div>
                                    <div class="filterBox">
                                        <span class="text">{{ $t('ManagementSearch.parts.dl12') }} </span>
                                        <label class="calendar2">
                                            <input type="date" :class="[selectedDate6 !== '' ? 'fillterText' : 'fillterText2 blank']" placeholder="日付を選択してください" v-model="selectedDate6" />
                                        </label>
                                    </div>
                                </dd>
                            </dl>
                            <dl>
                                <!--在職ステータス-->
                                <dt>
                                    <span>{{ $t('ManagementSearch.parts.dl13') }} </span>
                                </dt>
                                <dd>
                                    <ul class="flex">
                                        <li v-for="(data, index) in incumbentStatusInfo" :key="index">
                                            <input type="checkbox" :id="'data2-' + (index + 1)" class="checkbox" v-model="managementListRequest.IncumbentStatuses" :value="data.value" />
                                            <label class="checkbox label" :for="'data2-' + (index + 1)">{{ data.name }}</label>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl>
                                <!--組織-->
                                <dt>
                                    <span>{{ $t('ManagementSearch.parts.dl14') }} </span>
                                </dt>
                                <dd>
                                    <div class="select">
                                        <select v-model="managementListRequest.OrganizationPk" @change="getOrganizationIssue" required>
                                            <option v-for="organization in organizationIssue" :key="organization.pk" :value="organization.pk">
                                                {{ organization.name }}
                                            </option>
                                        </select>
                                    </div>
                                </dd>
                            </dl>
                            <dl>
                                <!--職位-->
                                <dt>
                                    <span>{{ $t('ManagementSearch.parts.dl15') }} </span>
                                </dt>
                                <dd>
                                    <div class="select">
                                        <select v-model="managementListRequest.PositionPk" required>
                                            <option value="">{{ $t('ManagementSearch.parts.dl20') }}</option>
                                            <option v-for="contents in managementListStore.positionIssue" :key="contents.pk" :value="contents.pk">
                                                {{ contents.name }}
                                            </option>
                                        </select>
                                    </div>
                                </dd>
                            </dl>
                            <dl>
                                <!--職種-->
                                <dt>
                                    <span>{{ $t('ManagementSearch.parts.dl16') }} </span>
                                </dt>
                                <dd>
                                    <div class="select">
                                        <select v-model="managementListRequest.JobCategoryPk" required>
                                            <option value="">{{ $t('ManagementSearch.parts.dl20') }}</option>
                                            <option v-for="contents in managementListStore.jobCategoryIssue" :key="contents.pk" :value="contents.pk">
                                                {{ contents.name }}
                                            </option>
                                        </select>
                                    </div>
                                </dd>
                            </dl>
                            <dl>
                                <!--雇用形態-->
                                <dt>
                                    <span>{{ $t('ManagementSearch.parts.dl17') }} </span>
                                </dt>
                                <dd>
                                    <div class="select">
                                        <select v-model="managementListRequest.EmploymentTypePk" required>
                                            <option value="">{{ $t('ManagementSearch.parts.dl20') }}</option>
                                            <option v-for="contents in managementListStore.employmentTypeIssue" :key="contents.pk" :value="contents.pk">
                                                {{ contents.name }}
                                            </option>
                                        </select>
                                    </div>
                                </dd>
                            </dl>
                            <dl>
                                <!--勤務地-->
                                <dt>
                                    <span>{{ $t('ManagementSearch.parts.dl18') }} </span>
                                </dt>
                                <dd>
                                    <div class="select">
                                        <select v-model="managementListRequest.LocationPk" required>
                                            <option value="">{{ $t('ManagementSearch.parts.dl20') }}</option>
                                            <option v-for="contents in managementListStore.locationIssue" :key="contents.pk" :value="contents.pk">
                                                {{ contents.name }}
                                            </option>
                                        </select>
                                    </div>
                                </dd>
                            </dl>
                            <dl>
                                <!--時間外労働時間-->
                                <dt>
                                    <span>{{ $t('ManagementSearch.parts.dl19') }} </span>
                                </dt>
                                <dd>
                                    <div class="select">
                                        <select v-model="managementListRequest.OvertimePk" required>
                                            <option value="">{{ $t('ManagementSearch.parts.dl20') }}</option>
                                            <option v-for="contents in managementListStore.overTimeIssue" :key="contents.pk" :value="contents.pk">
                                                {{ contents.name }}
                                            </option>
                                        </select>
                                    </div>
                                </dd>
                            </dl>
                        </section>
                    </section>
                    <section id="fillterFooter">
                        <button class="btnPrimary" id="" @click="getUserList" :disabled="disabledStore.isDisabled">{{ $t('ManagementSearch.parts.button1') }}</button>
                    </section>
                </section>
                <!--検索条件エリア-->
                <div class="whiteBox wide">
                    <h3 class="textCenter">
                        <span>{{ $t('ManagementList.parts.pageTitle1') }} </span>
                    </h3>
                    <section class="whiteBoxContents">
                        <ul class="btnArea flexLeft" style="margin-bottom: 10px">
                            <li style="margin-right: 10px">
                                <!--追加-->
                                <button class="btnPrimary" id="openModalUserListUserDetailEntry" @click="openDetail()">{{ $t('ManagementList.parts.button1') }}</button>
                            </li>
                            <li style="margin-right: 10px">
                                <!--CSVアップロード-->
                                <label class="btnPrimary button" :class="{ disabled: disabledStore.isDisabled }">
                                    <span><input type="file" id="file" @change="uploadCsv($event)" style="display: none" />{{ $t('ManagementList.parts.button2') }} </span>
                                </label>
                            </li>
                            <li style="margin-left: auto">
                                <!--検索-->
                                <button class="btnNormal" id="openFillterBlockBtn" @click="changeMoveinState">{{ $t('ManagementList.parts.button4') }}</button>
                            </li>
                        </ul>
                        <ul class="btnArea flexLeft">
                            <li style="margin-right: 10px">
                                <!--管理者ユーザーを利用者に追加-->
                                <button class="btnNormal" id="openModalUserSelection" @click="disclosureCounselingUserSelectOpen">{{ $t('ManagementList.parts.button3') }}</button>
                            </li>
                            <li style="margin-left: auto; margin-right: 10px">
                                <!--一覧表示した利用者に契約割り当て-->
                                <button class="btnNormal" id="openModalUserPlanControl" style="width: 250px" @click="openContract">{{ $t('ManagementList.parts.button5') }}</button>
                            </li>
                            <li>
                                <!--CSVダウンロード-->
                                <button class="btnNormal" @click="downloadUCsv" :disabled="disabledStore.isDisabled">{{ $t('ManagementList.parts.button6') }}</button>
                            </li>
                        </ul>
                        <article class="innerTable" style="max-height: calc(100% - 180px)">
                            <table class="table userTable noSelection" id="sample">
                                <thead>
                                    <tr>
                                        <th style="width: 150px">
                                            <!--ユーザーID-->
                                            <span>{{ $t('ManagementList.parts.tableHeader1') }} </span>
                                        </th>
                                        <th style="width: 150px">
                                            <!--メールアドレス-->
                                            <span>{{ $t('ManagementList.parts.tableHeader2') }} </span>
                                        </th>
                                        <th style="width: 150px">
                                            <!--氏名-->
                                            <span>{{ $t('ManagementList.parts.tableHeader3') }} </span>
                                        </th>
                                        <th style="width: 80px">
                                            <!--管理者登録-->
                                            <span>{{ $t('ManagementList.parts.tableHeader4') }} </span>
                                        </th>
                                        <th style="width: 80px">
                                            <!--利用停止-->
                                            <span>{{ $t('ManagementList.parts.tableHeader5') }} </span>
                                        </th>
                                        <th style="width: auto">
                                            <!--契約割り当て-->
                                            <span>{{ $t('ManagementList.parts.tableHeader6') }} </span>
                                        </th>
                                        <th style="width: 30px">
                                            <!--編集-->
                                            <span>{{ $t('ManagementList.parts.button7') }}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody v-for="contents in managementListStore.userList" :key="contents.index">
                                    <tr>
                                        <td>
                                            <span>{{ contents.UserId }}</span>
                                        </td>
                                        <td>
                                            <span>{{ contents.MailAddress }}</span>
                                        </td>
                                        <td>
                                            <span>{{ contents.UserNm }}</span>
                                        </td>
                                        <td>
                                            <span>
                                                <div v-if="contents.MngFlag">{{ $t('ManagementList.parts.dl1') }}</div>
                                                <div v-else>{{ $t('ManagementList.parts.dl2') }}</div>
                                            </span>
                                        </td>
                                        <td>
                                            <span>
                                                <div v-if="contents.SuspendFlag">{{ $t('ManagementList.parts.dl3') }}</div>
                                            </span>
                                        </td>
                                        <td>
                                            <span>{{ contents.ContractInfo }}</span>
                                        </td>
                                        <td>
                                            <!--編集-->
                                            <button type="button" class="btnEdit" id="openModalUserListUserDetailUpdate" @click="openDetail(contents.UserPk)">{{ $t('ManagementList.parts.button7') }}</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </article>
                        <Pagination ref="pagination" :currentPage="currentPage" :totalPages="totalPages" @pageChanged="handlePageChange" />
                    </section>
                    <section class="whiteBoxFooter">
                        <ul class="btnArea center">
                            <li>
                                <RouterLink :to="{ name: 'home' }">
                                    <button type="button" id="btnBack" class="btnNormal">{{ $t('ManagementList.parts.button8') }}</button>
                                </RouterLink>
                            </li>
                        </ul>
                    </section>
                </div>
            </div>
        </div>
    </section>
</template>

<style></style>
